import React, { useState, useEffect, useRef } from "react";
import "../../App.css";
import { Auth } from '../../core/auth';
import { OTPService } from '../../core/otp-service';
import { AuthLogo } from '../../components/AuthLogo';
import { Icon } from '../../components/Icon';

function ForgotPasswordPage({ onBack, showToast }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const sendEmail = async () => {
    setError("");
    if (!email.trim().includes("@")) { setError("Enter a valid email address"); return false; }
    setLoading(true);
    const result = await OTPService.sendEmail("forgot", email);
    setLoading(false);
    if (!result.success) { setError(result.error); return false; }
    setOtpSent(true);
    setCountdown(30);
    setOtp(["", "", "", "", "", ""]);
    if (result.demo) showToast(`Demo OTP: ${result.otp} (see console)`, "info");
    else showToast(`OTP sent to ${email.trim()}`, "success");
    return true;
  };

  const verifyOtp = async () => {
    setError("");
    const entered = otp.join("");
    if (entered.length < 6) { setError("Enter all 6 digits"); return; }
    setLoading(true);
    const result = await OTPService.verifyEmail("forgot", email, entered);
    setLoading(false);
    if (!result.success) { setError(result.error); return; }
    setResetToken(result.token);
    setStep(3);
  };

  const submitReset = async () => {
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      const result = await Auth.resetPassword(resetToken, password);
      if (result.error) { setError(result.error); return; }
      showToast("Password reset successfully", "success");
      setStep(4);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 5) otpRefs[i + 1].current?.focus();
    if (!val && i > 0) otpRefs[i - 1].current?.focus();
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 13, marginBottom: 16 }}>← Back to login</button>

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <AuthLogo size={72} />
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 24, marginBottom: 4 }}>Reset Password</div>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>
            {step === 1 && "Enter your registered email"}
            {step === 2 && "Verify with a one-time code"}
            {step === 3 && "Choose a new password"}
            {step === 4 && "You're all set"}
          </div>
        </div>

        {step === 1 && (
          <>
            <div className="f-group" style={{ marginBottom: 16 }}>
              <label className="f-label">Email Address</label>
              <input className="f-input" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 12, padding: "8px 12px", background: "#FFF1F2", borderRadius: 6 }}>{error}</div>}
            <button
              className="btn btn-primary btn-lg"
              style={{ width: "100%" }}
              onClick={async () => { if (await sendEmail()) setStep(2); }}
              disabled={loading || !email.trim().includes("@")}
            >
              Send OTP to Email
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
              Enter the 6-digit code sent to {email.trim()}
            </div>
            <div className="otp-grid">
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={otpRefs[i]}
                  className="f-input otp-box"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                />
              ))}
            </div>
            {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 12 }}>{error}</div>}
            <button className="btn btn-primary btn-lg" style={{ width: "100%", marginBottom: 10 }} onClick={verifyOtp} disabled={loading}>
              {loading ? "Verifying…" : "Verify OTP →"}
            </button>
            <button
              className="btn btn-sm"
              style={{ width: "100%", marginBottom: 8 }}
              disabled={countdown > 0 || loading}
              onClick={sendEmail}
            >
              {countdown > 0 ? `Resend in ${countdown}s` : "Resend OTP"}
            </button>
            <button className="btn btn-sm" style={{ width: "100%" }} onClick={() => { setStep(1); setOtpSent(false); setError(""); }}>
              ← Edit email
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <div className="f-group" style={{ marginBottom: 14 }}>
              <label className="f-label">New Password</label>
              <input className="f-input" type="password" placeholder="Minimum 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="f-group" style={{ marginBottom: 16 }}>
              <label className="f-label">Confirm Password</label>
              <input className="f-input" type="password" placeholder="Repeat password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitReset()} />
            </div>
            {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 12 }}>{error}</div>}
            <button className="btn btn-primary btn-lg" style={{ width: "100%" }} onClick={submitReset} disabled={loading}>
              {loading ? "Saving…" : "Reset Password →"}
            </button>
          </>
        )}

        {step === 4 && (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ marginBottom: 16, color: "var(--green)" }}><Icon name="success" size={52} /></div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, marginBottom: 16 }}>Password Updated</div>
            <button className="btn btn-primary btn-lg" style={{ width: "100%" }} onClick={onBack}>Back to Login →</button>
          </div>
        )}
      </div>
    </div>
  );
}

export { ForgotPasswordPage };
